// src/components/organisms/VideoPlayerWithProgressBar.js

import React, { useRef, useState } from "react";
import { Box } from "@mui/material";
import VideoPlayer from "../molecules/VideoPlayer";
import CustomProgressBar from "../molecules/CustomProgressBar";
import InteractionDisplay from "../organisms/InteractionDisplay";
import PropTypes from "prop-types";

const VideoPlayerWithProgressBar = ({
  src,
  interactions,
  onAddInteraction,
  onTimeUpdate, // Function to lift currentTime up
}) => {
  const videoPlayerRef = useRef(null); // Ref to VideoPlayer
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [visibleInteractions, setVisibleInteractions] = useState([]);

  const handleTimeUpdate = (time) => {
    setCurrentTime(time);
    if (onTimeUpdate) {
      onTimeUpdate(time); // Report time upwards
    }

    // Check if any interactions should be displayed
    interactions.forEach((interaction) => {
      if (
        Math.abs(time - interaction.timestamp) < 0.5 && // Adjust the threshold as needed
        !visibleInteractions.includes(interaction.id)
      ) {
        setVisibleInteractions((prev) => [...prev, interaction.id]);
      }
    });
  };

  const handleLoadedMetadata = (duration) => {
    setDuration(duration);
  };

  const handleSeek = (value) => {
    if (
      videoPlayerRef.current &&
      isFinite(value) &&
      value >= 0 &&
      value <= duration
    ) {
      videoPlayerRef.current.seekTo(value);
      setCurrentTime(value);
      if (onTimeUpdate) {
        onTimeUpdate(value); // Report time upwards
      }
    } else {
      console.warn("Attempted to seek to an invalid time:", value);
    }
  };

  const handleMarkerClick = (timestamp) => {
    handleSeek(timestamp);
  };

  const handleAddInteractionClick = (timestamp) => {
    if (isFinite(timestamp) && timestamp >= 0 && timestamp <= duration) {
      onAddInteraction(timestamp); // Pass the clicked timestamp to open modal
    } else {
      console.warn("Clicked timestamp is invalid:", timestamp);
    }
  };

  const handleInteractionClose = (interactionId) => {
    setVisibleInteractions((prev) => prev.filter((id) => id !== interactionId));
  };

  return (
    <Box>
      <Box position="relative" width="100%">
        <VideoPlayer
          ref={videoPlayerRef}
          src={src}
          onTimeUpdate={handleTimeUpdate} // Receive time updates from VideoPlayer
          onLoadedMetadata={handleLoadedMetadata} // Receive duration
        />
        {/* Interaction Display */}
        <InteractionDisplay
          interactions={interactions.filter((interaction) =>
            visibleInteractions.includes(interaction.id)
          )}
          currentTime={currentTime}
          onInteractionClose={handleInteractionClose}
        />
      </Box>
      {/* Custom Progress Bar */}
      <CustomProgressBar
        currentTime={currentTime}
        duration={duration}
        interactions={interactions}
        onSeek={handleSeek}
        onMarkerClick={handleMarkerClick}
        onAddInteraction={handleAddInteractionClick} // Handle adding interaction from progress bar
      />
    </Box>
  );
};

VideoPlayerWithProgressBar.propTypes = {
  src: PropTypes.string.isRequired,
  interactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      mediaURL: PropTypes.string,
      mediaType: PropTypes.oneOf(["image", "video"]),
      callToAction: PropTypes.string,
      callToActionURL: PropTypes.string,
      timestamp: PropTypes.number.isRequired,
    })
  ).isRequired,
  onAddInteraction: PropTypes.func.isRequired, // Function to open modal with timestamp
  onTimeUpdate: PropTypes.func.isRequired, // Function to lift currentTime up
};

export default VideoPlayerWithProgressBar;
